import React, { useCallback, useEffect, useRef } from "../../__compat__/react/main.entry.mjs.mjs";
import { useMergedRef } from "../../hooks/useMergedRef.mjs.mjs";
import { useSpacing } from "../../api/spacing/useSpacing.mjs.mjs";
import { useIndicator } from "./useIndicator.mjs.mjs";
import { useScroll } from "./useScroll.mjs.mjs";
import { StyledList, StyledItem, StyledBlockIndicator, StyledLineIndicator, StyledScrollContainer, StyledScrollControl, StyledScrollIcon, StyledContainer, } from "./GenericNavigation.styles.mjs.mjs";
// This number represents the percentage of the scrollable area to scroll by.
const scrollMultiplier = 0.75;
const noop = () => { };
export const GenericNavigationList = React.forwardRef(({ 'data-testid': testId, children, className, id, size = 'large', variant = 'text', scrollButtons = 'auto', selectionFollowsFocus = false, itemType = GenericNavigationItem, selected, onSelect = noop, scrollButtonProps, ...restProps }, ref) => {
    const isItem = useCallback((element) => React.isValidElement(element) && element.type === itemType, [itemType]);
    const listRef = useRef(null);
    const mergedListRef = useMergedRef(listRef, ref);
    const selectedItemRef = useRef(null);
    const handleSelect = useCallback((nextSelected, event) => {
        var _a, _b;
        const disabled = (_b = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`#${nextSelected} a`)) === null || _b === void 0 ? void 0 : _b.hasAttribute('aria-disabled');
        if (!disabled) {
            onSelect(nextSelected, event);
        }
    }, [listRef, onSelect]);
    const makeClickHandler = (nextSelected) => {
        return (event) => {
            if (nextSelected !== selected)
                handleSelect(nextSelected, event);
        };
    };
    const isRtl = (listRef === null || listRef === void 0 ? void 0 : listRef.current) && getComputedStyle(listRef === null || listRef === void 0 ? void 0 : listRef.current).direction === 'rtl';
    const keyDownHandler = (event) => {
        var _a, _b;
        if (event.key === 'Tab')
            return;
        event.preventDefault();
        const navItems = Array.from((_b = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`${StyledItem} a`)) !== null && _b !== void 0 ? _b : []);
        const focusIndex = navItems.findIndex((element) => element === event.target);
        if ([' ', 'Enter'].includes(event.key)) {
            handleSelect(navItems[focusIndex].parentElement.getAttribute('id'), event);
            return;
        }
        const prevKey = isRtl ? 'ArrowRight' : 'ArrowLeft';
        const nextKey = isRtl ? 'ArrowLeft' : 'ArrowRight';
        let nextFocusIndex = focusIndex;
        switch (event.key) {
            case 'Home':
                nextFocusIndex = 0;
                break;
            case 'End':
                nextFocusIndex = navItems.length - 1;
                break;
            case prevKey:
                nextFocusIndex =
                    focusIndex === 0 ? navItems.length - 1 : focusIndex - 1;
                break;
            case nextKey:
                nextFocusIndex = (focusIndex + 1) % navItems.length;
                break;
        }
        if (nextFocusIndex !== focusIndex) {
            const nextSelected = navItems[nextFocusIndex];
            if (nextSelected)
                nextSelected.focus();
            if (selectionFollowsFocus) {
                handleSelect(navItems[nextFocusIndex].parentElement.getAttribute('id'), event);
            }
        }
    };
    const indicatorReady = useIndicator(listRef, selectedItemRef, [
        selected,
        size,
    ]);
    const scrollContainerRef = useRef(null);
    const scrollContentsRef = useRef(null);
    const { canScroll, canScrollBackward, canScrollForward, scrollIntoView, scrollBy, } = useScroll(scrollContainerRef, scrollContentsRef, [size]);
    const showScrollButtons = scrollButtons === 'always' || (scrollButtons === 'auto' && canScroll);
    const scrollByViewWidth = useCallback((multiplier) => {
        return () => scrollContainerRef.current &&
            scrollBy({
                left: scrollContainerRef.current.offsetWidth * multiplier,
                behavior: 'smooth',
            });
    }, [scrollContainerRef, scrollBy]);
    const disableScrollPrevious = !canScrollBackward;
    const disableScrollNext = !canScrollForward;
    const scrollPrevious = scrollByViewWidth(isRtl ? scrollMultiplier : -scrollMultiplier);
    const scrollNext = scrollByViewWidth(isRtl ? -scrollMultiplier : scrollMultiplier);
    useEffect(() => {
        scrollIntoView(selectedItemRef);
    }, [scrollIntoView, selected]);
    return (React.createElement(StyledContainer, { className: className, "data-testid": testId, id: id, outerSpacing: useSpacing(restProps), ref: mergedListRef, size: size, "aria-label": restProps['aria-label'] },
        showScrollButtons && (React.createElement(StyledScrollControl, { position: "start", "aria-label": scrollButtonProps === null || scrollButtonProps === void 0 ? void 0 : scrollButtonProps.start['aria-label'], "aria-hidden": scrollButtonProps === null || scrollButtonProps === void 0 ? void 0 : scrollButtonProps.start['aria-hidden'], tabIndex: scrollButtonProps === null || scrollButtonProps === void 0 ? void 0 : scrollButtonProps.start.tabIndex, ...(disableScrollPrevious && {
                disabled: true,
            }), onClick: scrollPrevious },
            React.createElement(ScrollIcon, null))),
        React.createElement(StyledScrollContainer, { as: variant === 'text' ? 'nav' : 'div', ref: scrollContainerRef },
            indicatorReady && variant === 'tabs' && React.createElement(StyledBlockIndicator, null),
            React.createElement(StyledList, { role: variant === 'tabs' ? 'tablist' : undefined, ref: scrollContentsRef }, React.Children.map(children, (child) => {
                if (isItem(child)) {
                    const isSelected = child.props.id === selected;
                    const modified = React.cloneElement(child, {
                        selected: isSelected,
                        onClick: makeClickHandler(child.props.id),
                        onKeyDown: keyDownHandler,
                        ...(isSelected && { ref: selectedItemRef }),
                        variant,
                    });
                    return modified;
                }
                else {
                    return child;
                }
            })),
            indicatorReady && React.createElement(StyledLineIndicator, null)),
        showScrollButtons && (React.createElement(StyledScrollControl, { position: "end", "aria-label": scrollButtonProps === null || scrollButtonProps === void 0 ? void 0 : scrollButtonProps.end['aria-label'], "aria-hidden": scrollButtonProps === null || scrollButtonProps === void 0 ? void 0 : scrollButtonProps.end['aria-hidden'], tabIndex: scrollButtonProps === null || scrollButtonProps === void 0 ? void 0 : scrollButtonProps.end.tabIndex, ...(disableScrollNext && {
                disabled: true,
            }), onClick: scrollNext },
            React.createElement(ScrollIcon, null)))));
});
GenericNavigationList.displayName = 'GenericNavigationList';
export const GenericNavigationItem = React.forwardRef(({ children, id, selected, onClick = noop, onKeyDown = noop, variant = 'text', controlRef, controlId, 'aria-controls': ariaControls, disabled, href, }, ref) => {
    const handleClick = useCallback((event) => {
        if (!href)
            event.preventDefault();
        onClick(event);
    }, [href, onClick]);
    const props = {
        onClick: handleClick,
        ref: controlRef,
        id: controlId,
        href: href !== null && href !== void 0 ? href : '#',
        ...(disabled && {
            /* Do not use 'disabled' HTML attribute; keep item focusable per WAI guidelines */
            'aria-disabled': true,
        }),
        ...(variant === 'tabs'
            ? {
                role: 'tab',
                'aria-controls': ariaControls,
                'aria-selected': selected,
                onKeyDown,
                /* Note: tabIndex here refers to navigation by the "tab" key on the keyboard.
                 * Only the selected tab should be in the page's tab sequence */
                tabIndex: selected ? 0 : -1,
            }
            : {
                ...(!href && { role: 'button' }),
                ...(selected && { 'aria-current': true }),
            }),
    };
    return (React.createElement(StyledItem, { ...(variant === 'tabs' && { role: 'presentation' }), variant: variant, id: id, ref: ref },
        React.createElement("a", { ...props }, children)));
});
GenericNavigationItem.displayName = 'GenericNavigationItem';
const ScrollIcon = () => (React.createElement(StyledScrollIcon, { width: "24", height: "24", viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M10 7L15 12 10 17", stroke: "currentColor", strokeWidth: "1", fill: "none", fillRule: "evenodd" })));
//# sourceMappingURL=GenericNavigation.mjs.mjs.map